import {
  DashboardOutlined,
  TeamOutlined,
  DollarOutlined,
  MailOutlined,
  AccountBookOutlined,
  WindowsOutlined,
  CheckSquareOutlined,
  ApartmentOutlined,
  CloudOutlined,
  ApiOutlined,
  InboxOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CarOutlined,
  SettingOutlined,
  BellOutlined,
  FileTextOutlined,
  PieChartOutlined,
  MessageOutlined,
  RadarChartOutlined,
  DatabaseOutlined,
  BarChartOutlined,
  HeatMapOutlined,
} from '@ant-design/icons';
import { Badge } from 'antd';
import type { MenuProps } from 'antd';
import type { ReactNode } from 'react';
import type {
  WarehouseMenuDTO,
  WarehouseMenuItemDTO,
  TaskMenuDTO,
  TaskMenuItemDTO,
  TaskMenuStatsDTO,
} from '@shared/api/menu';
import type { CableLineTypeRef } from '@features/cable/types';
import type { ExtendedMenuItem } from '@shared/types/menu';
import { buildWarehouseFallbackMenuItems } from './warehouseMenuFallback';
import { menuAliasKey } from './menuAlias';
import { SETTINGS_MENU_ENTRIES } from './settingsMenuItems';
import { erpNavigationGate } from './erpNavigationGates';

export type MenuItem = Required<MenuProps>['items'][number];

type BadgeDTO = {
  type: string;
  value: number;
  status?: string;
};

function getItem(
  label: ReactNode,
  key: string,
  icon?: ReactNode,
  children?: MenuItem[],
  type?: 'group',
  extra?: Partial<MenuItem> & {
    requiredPermission?: string;
    requiredAnyPermissions?: string[];
    requiredRole?: string;
    requiresGatedChild?: boolean;
    requiredErpChapter?: string;
    requiredErpSubChapter?: string;
  }
): ExtendedMenuItem {
  return {
    key,
    icon,
    children,
    label,
    type,
    ...extra,
  } as ExtendedMenuItem;
}

interface MenuCounts {
  tasks?: { my: number; all: number };
  messages?: { unread: number; new: number };
  inventory?: number;
}

interface MenuOptions {
  warehouseMenu?: WarehouseMenuDTO | null;
  warehouseStatus?: 'loading' | 'error' | 'ready';
  devicesChildren?: MenuItem[];
  taskMenu?: TaskMenuDTO | null;
  cableMenu?: CableMenuState;
  mapCities?: Array<{ id: number; name: string; centerGeo?: string }>; // Список городов для быстрого перехода

  /**
   * Есть ли в инсталляции источник метрики «активность в интернете» — хотя бы один
   * автоматический биллинг с включённым флагом обновления активности.
   * Приезжает из GET /customers/activity/sources (один запрос в кеше react-query),
   * тем же приёмом, что и флаги пункта устройств.
   *
   * Пункта нет, пока флаг не пришёл: показать пункт, за которым у всех «нет
   * данных», хуже, чем показать его на секунду позже.
   */
  hasInternetActivitySource?: boolean;
}

type CableMenuType = Pick<CableLineTypeRef, 'id' | 'name' | 'specialType'>;

interface CableMenuState {
  status: 'loading' | 'error' | 'ready';
  types?: CableMenuType[] | null;
}

type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

// Функция для получения URL Grafana (прямой доступ на порт 3000)
function getGrafanaUrl(): string {
  // Используем переменную окружения или определяем URL динамически
  const envUrl = import.meta.env.VITE_GRAFANA_URL;
  if (envUrl) {
    // Если в envUrl уже есть путь, используем как есть
    return envUrl;
  }
  
  // Определяем IP сервера из текущего хоста
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Для localhost используем IP сервера (можно настроить через env)
    const serverIp = import.meta.env.VITE_SERVER_IP || '10.0.21.240';
    return `http://${serverIp}:3000`;
  }
  
  // Используем текущий хост с портом 3000
  return `http://${hostname}:3000`;
}

const badgeStatusColorMap: Record<string, string> = {
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  info: '#1677ff',
};

const warehouseIconMap: Record<string, ReactNode> = {
  InboxOutlined: <InboxOutlined />,
  FileTextOutlined: <FileTextOutlined />,
  PieChartOutlined: <PieChartOutlined />,
  BellOutlined: <BellOutlined />,
  DatabaseOutlined: <DatabaseOutlined />,
  MailOutlined: <MailOutlined />,
  TeamOutlined: <TeamOutlined />,
  SettingOutlined: <SettingOutlined />,
};

function resolveWarehouseIcon(name?: string): ReactNode | undefined {
  if (!name) {
    return undefined;
  }
  return warehouseIconMap[name] ?? undefined;
}

function decorateLabelWithBadge(label: ReactNode, badge?: BadgeDTO, tooltip?: string): ReactNode {
  if (!badge) {
    return label;
  }

  const color = badgeStatusColorMap[badge.status ?? 'info'] ?? '#1677ff';

  return (
    <span className="inline-flex items-center gap-2">
      {label}
      <Badge
        count={badge.value}
        size="small"
        overflowCount={999}
        style={{ backgroundColor: color }}
        title={tooltip}
      />
    </span>
  );
}

function mapWarehouseMenuItem(item: WarehouseMenuItemDTO, t: TranslateFn): MenuItem {
  // Конвертируем формат "menu.warehouse" → "menu:warehouse" для namespace
  const translationKey = item.labelKey?.startsWith('menu.')
    ? item.labelKey.replace('menu.', 'menu:')
    : item.labelKey;
  const labelText = translationKey ? t(translationKey) : item.label ?? item.path ?? item.key;
  const label = decorateLabelWithBadge(labelText, item.badge);
  const rawKey = item.key || item.path || labelText;
  const key =
    item.external && item.path ? `external::${item.path}` : rawKey ?? `warehouse-item-${Math.random()}`;

  const children = item.children?.map((child) => mapWarehouseMenuItem(child, t));

  return getItem(label, key, resolveWarehouseIcon(item.icon), children);
}

function buildWarehouseMenu(
  menu: WarehouseMenuDTO | null | undefined,
  t: TranslateFn,
) {
  if (!menu || !menu.items || menu.items.length === 0) {
    return null;
  }

  // Конвертируем формат "menu.warehouse" → "menu:warehouse" для namespace
  const labelTranslationKey = menu.labelKey?.startsWith('menu.')
    ? menu.labelKey.replace('menu.', 'menu:')
    : menu.labelKey;
  const label = labelTranslationKey ? t(labelTranslationKey) : menu.key;
  const items = menu.items.map((item) => mapWarehouseMenuItem(item, t));

  return { label, items };
}

function sortCableTypes(types: CableMenuType[] = []): CableMenuType[] {
  return [...types].sort((a, b) => a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }));
}

function buildCableTypeItems(
  types: CableMenuType[] | undefined | null,
  t: TranslateFn,
): MenuItem[] {
  if (!types || types.length === 0) {
    return [
      getItem(
        t('menu:cable_types_empty'),
        'cable::types-empty',
        undefined,
        undefined,
        undefined,
        { disabled: true },
      ),
    ];
  }

  return sortCableTypes(types).map((type) =>
    getItem(
      type.name,
      `cable::type-${type.id}`,
      undefined,
      [
        getItem(
          t('menu:cable_type_lines', { type: type.name }),
          `/cable/lines?typeId=${type.id}`,
        ),
        getItem(
          t('menu:cable_type_trunks', { type: type.name }),
          `/cable/trunks?typeId=${type.id}`,
        ),
      ],
      'group',
    ),
  );
}

function buildCableMenuChildren(menu: CableMenuState | undefined, t: TranslateFn): MenuItem[] {
  const items: MenuItem[] = [
    getItem(t('menu:cable_lines'), '/cable/lines'),
    getItem(t('menu:cable_trunks'), '/cable/trunks'),
  ];

  if (menu) {
    if (menu.status === 'loading') {
      items.push(
        getItem(
          t('menu:cable_menu_loading'),
          'cable::loading',
          undefined,
          undefined,
          undefined,
          { disabled: true },
        ),
      );
    } else if (menu.status === 'error') {
      items.push(
        getItem(
          t('menu:cable_menu_unavailable'),
          'cable::unavailable',
          undefined,
          undefined,
          undefined,
          { disabled: true },
        ),
      );
    } else {
      items.push(
        getItem(
          t('menu:cable_types_group'),
          'cable::types',
          undefined,
          buildCableTypeItems(menu.types, t),
        ),
      );
    }
  }

  items.push(
    getItem(t('menu:cable_duct'), '/cable/duct'),
    getItem(t('menu:cable_crosses'), '/cable/crosses'),
    getItem(t('menu:cable_splitters'), '/cable/splitters'),
  );

  return items;
}

function buildTaskBadgeTitle(
  t: TranslateFn,
  stats?: TaskMenuStatsDTO
): string | undefined {
  if (!stats) {
    return undefined;
  }

  const parts: string[] = [];
  if (typeof stats.active === 'number') {
    parts.push(t('menu:tasks_badge_active', { count: stats.active }));
  }
  if (typeof stats.overdue === 'number') {
    parts.push(t('menu:tasks_badge_overdue', { count: stats.overdue }));
  }

  return parts.length ? parts.join(' · ') : undefined;
}

function mapTaskMenuItem(item: TaskMenuItemDTO, t: TranslateFn): MenuItem {
  // Конвертируем формат "menu.tasks" → "menu:tasks" для namespace
  const translationKey = item.labelKey?.startsWith('menu.')
    ? item.labelKey.replace('menu.', 'menu:')
    : item.labelKey;
  const labelText = translationKey ? t(translationKey) : item.label ?? item.path ?? item.key;
  const badgeTitle = buildTaskBadgeTitle(t, item.stats);
  const label = decorateLabelWithBadge(labelText, item.badge, badgeTitle);

  let key = item.key || labelText;

  if (item.external && item.path) {
    key = `external::${item.path}`;
  } else if (item.path) {
    key = item.path;
  }

  const children = item.children?.map((child) => mapTaskMenuItem(child, t));

  return getItem(label, key, undefined, children);
}

function buildTaskMenu(menu: TaskMenuDTO | null | undefined, t: TranslateFn) {
  if (!menu || !menu.items || menu.items.length === 0) {
    return null;
  }

  // Конвертируем формат "menu.tasks" → "menu:tasks" для namespace
  const labelTranslationKey = menu.labelKey?.startsWith('menu.')
    ? menu.labelKey.replace('menu.', 'menu:')
    : menu.labelKey;
  const label = labelTranslationKey ? t(labelTranslationKey) : menu.key;
  const items = menu.items.map((item) => mapTaskMenuItem(item, t));

  return { label, items };
}

function getDefaultTaskMenuItems(t: TranslateFn): MenuItem[] {
  return [
    getItem(t('menu:tasks_create'), '/tasks?action=create'),
    getItem(t('menu:tasks_list'), '/tasks'),
    getItem(t('menu:tasks_my'), '/tasks?assignee=me'),
    getItem(t('menu:tasks_calendar'), '/tasks?view=calendar'),
    getItem(t('menu:tasks_schedule'), '/tasks/schedule'),
  ];
}

export const getDefaultDevicesMenuItems = (t: TranslateFn): MenuItem[] => [
  getItem(t('menu:devices_switches'), '/equipment/devices/switches'),
  getItem(t('menu:devices_olt'), '/equipment/devices/olt'),
  getItem(t('menu:devices_onu'), '/equipment/devices/onu'),
  getItem(t('menu:devices_radio'), '/equipment/devices/radio'),
  getItem(t('menu:devices_medical'), '/equipment/devices/medical'),
  getItem(t('menu:devices_system'), '/equipment/devices/system'),
  getItem(t('menu:devices_cwdm'), '/equipment/devices/cwdm'),
  getItem(t('menu:devices_vlan'), '/equipment/devices/vlan', undefined, undefined, undefined, {
    requiredPermission: 'devices.view',
  }),
  getItem(
    t('menu:devices_iface_error_log'),
    '/equipment/devices/iface-errors',
    undefined,
    undefined,
    undefined,
    { requiredPermission: 'devices.view' },
  ),
  // ⚠️ ЗАПАСНОЙ набор: читается только когда серверная навигация пуста
  // (`get_navigation.go` — настоящий носитель, урок цикла 206). Держим строку рядом с
  // соседом, чтобы у оператора без серверной навигации экран тоже был достижим.
  getItem(
    t('menu:devices_firmware_report'),
    '/equipment/devices/firmware-report',
    undefined,
    undefined,
    undefined,
    { requiredPermission: 'devices.view' },
  ),
  getItem(
    t('menu:devices_peleng'),
    '/equipment/devices/peleng',
    undefined,
    undefined,
    undefined,
    { requiredPermission: 'devices.view' },
  ),
  getItem(
    t('menu:equipment_catalog'),
    'equipment-catalog-group',
    <ApiOutlined />,
    [
      getItem(t('menu:equipment_models'), '/equipment/models'),
      getItem(t('menu:equipment_webhooks'), '/equipment/webhooks'),
      getItem(t('menu:equipment_unknown_fingerprints'), '/equipment/unknown-fingerprints'),
    ],
    'group'
  ),
];

export const getMenuItemsWithCounts = (
  t: TranslateFn,
  counts?: MenuCounts,
  options?: MenuOptions
): MenuItem[] => {
  const taskMenuResult = buildTaskMenu(options?.taskMenu, t);
  const taskChildren =
    taskMenuResult?.items && taskMenuResult.items.length > 0
      ? taskMenuResult.items
      : getDefaultTaskMenuItems(t);
  const deviceMenuChildren = options?.devicesChildren ?? getDefaultDevicesMenuItems(t);
  const hasTaskMenuItems = taskChildren.length > 0 && taskMenuResult?.items?.length;
  // Состав и гейты — из единого реестра (shared/config/settingsMenuItems.ts), чтобы
  // menuConfig.tsx и menuConfigExtended.tsx не могли разойтись.
  const settingsChildren: MenuItem[] = SETTINGS_MENU_ENTRIES.map((entry) =>
    getItem(t(entry.labelKey), entry.key, undefined, undefined, undefined, {
      ...(entry.permission ? { requiredPermission: entry.permission } : {}),
      ...(entry.anyPermissions ? { requiredAnyPermissions: entry.anyPermissions } : {}),
      ...(entry.role ? { requiredRole: entry.role } : {}),
    }),
  );

  const warehouseMenuResult = buildWarehouseMenu(options?.warehouseMenu, t);
  const warehouseStatus = options?.warehouseStatus ?? 'ready';
  const warehouseLabel = warehouseMenuResult?.label ?? t('menu:warehouse');

  const fallbackWarehouseChildren: MenuItem[] = buildWarehouseFallbackMenuItems(t, (label, path) =>
    getItem(label, path),
  );

  let warehouseChildren: MenuItem[];
  const hasDynamicWarehouseItems = Boolean(warehouseMenuResult?.items?.length);

  if (warehouseStatus === 'loading') {
    const placeholderLabel = t('menu:warehouse_loading');
    warehouseChildren = [
      getItem(placeholderLabel, 'warehouse::placeholder', undefined, undefined, undefined, {
        disabled: true,
      }),
    ];
  } else if (warehouseStatus === 'ready' && hasDynamicWarehouseItems) {
    warehouseChildren = warehouseMenuResult!.items!;
  } else if (warehouseStatus === 'error') {
    warehouseChildren = fallbackWarehouseChildren;
  } else {
    // ready с пустым массивом или прочие случаи — показываем базовые разделы
    warehouseChildren = fallbackWarehouseChildren;
  }

  return [
  // Главная страница
  getItem(t('menu:dashboard'), '/dashboard', <DashboardOutlined />),

  // Абоненты
  getItem(
    t('menu:customers'),
    'customers',
    <TeamOutlined />,
    [
      getItem(t('menu:customers_create'), '/customers?action=create'),
      getItem(t('menu:customers_list'), '/customers'),
      // Гейт по праву (владелец, 2026-08-31): бэкенд теперь требует customers.ip_network
      // на чтение (парity с v4 RIGHT_CUSTOMER_IP_NET), пункт без гейта вёл бы оператора
      // без права на экран, отвечающий 403.
      getItem(t('menu:customers_ip_net'), '/customers/ip-net', undefined, undefined, undefined, {
        requiredPermission: 'customers.ip_network',
      }),
      getItem(t('menu:customers_debtors'), '/customers/debtors'),
      // Обе метрики живут на ОДНОМ экране /customers/activity, переключателем
      // источника. Пункт «в интернете» показывается, только если в инсталляции
      // есть источник; ключ — ПСЕВДОНИМ, потому что путь уже занят пунктом «в
      // сети», а antd на дубле key молча перестаёт рисовать один из двух пунктов.
      // Предустановка источника едет navigation state'ом (useSidebarNavigation).
      ...(options?.hasInternetActivitySource
        ? [getItem(t('menu:customers_activity_internet'), menuAliasKey('/customers/activity'))]
        : []),
      getItem(t('menu:customers_activity_network'), '/customers/activity'),
      getItem(t('menu:customers_billing_status'), '/customers/billing-status'),
      // Гейт по праву (growth-charts.1, 2026-09-01) — тот же, что в menuConfig.tsx:
      // customers.analytics.activity, бридж legacy права 24.
      getItem(t('menu:customers_charts_activity'), '/customers/charts/activity', undefined, undefined, undefined, {
        requiredPermission: 'customers.analytics.activity',
      }),
      // Гейт по праву (growth-charts.2, 2026-09-02): customers.analytics.growth, бридж
      // legacy права 25 (RIGHT_CUSTOMER_GRAF_INCREASE). Тот же гейт стоит на роуте и на
      // бэкенд-ручке. Без него пункт вёл бы на честный 403 у операторов без права.
      getItem(t('menu:customers_charts_growth'), '/customers/charts/growth', undefined, undefined, undefined, {
        requiredPermission: 'customers.analytics.growth',
      }),
      getItem(t('menu:customers_top100'), '/customers?preset=top100'),
      getItem(t('menu:customers_lost'), '/customers?preset=lost'),
      getItem(t('menu:customers_disconnected'), '/customers?preset=disconnected'),
      // Раскладка «с меткой / без метки» закрыта тем же правом, что ручка на сервере и
      // ProtectedRoute на маршруте: в v4 пункт закрыт RIGHT_CUSTOMER_MARK_READ = 33
      // (BodyHtml.php:1045). Пункт без гейта вёл бы оператора без права в редирект на главную.
      getItem(t('menu:customers_tags'), '/customers/tags', undefined, undefined, undefined, {
        requiredPermission: 'customers.mark.read',
      }),
      getItem(t('menu:customers_ex'), '/customers?preset=ex'),
      getItem(t('menu:customers_unknown_mac'), '/customers/unknown-mac', undefined, undefined, undefined, {
        requiredPermission: 'devices.view',
      }),
      getItem(t('menu:customers_sms_list'), '/customers/sms-list'),
      getItem(t('menu:customers_potential'), '/customers?preset=potential'),
    ],
    undefined,
    { requiredPermission: 'customers.view', ...erpNavigationGate('customers') }
  ),

  // Тарифы
  getItem(
    t('menu:tariffs'),
    'tariffs',
    <DollarOutlined />,
    [
      getItem(t('menu:tariffs_list'), '/tariffs'),
      getItem(t('menu:tariffs_groups'), '/tariffs/groups'),
      // Гейт по праву: экран читает /tariffs/traffic под customers.view, и без
      // права пункт вёл бы на честный отказ. Право 52 из v4 в мост не заведено.
      getItem(t('menu:tariffs_traffic'), '/tariffs/traffic', undefined, undefined, undefined, {
        requiredPermission: 'customers.view',
      }),
      // Услуги в V5 живут на работающем экране настроек; ключ — псевдоним, а не
      // адрес с параметром. Пояснение — в shared/config/menuAlias.ts.
      getItem(
        t('menu:settings_additional_services'),
        menuAliasKey('/settings/additional-services'),
      ),
    ],
    undefined,
    { requiredPermission: 'customers.view', ...erpNavigationGate('tariffs') }
  ),

  // Сообщения (с счётчиками)
  getItem(
    counts?.messages ? (
      <span>
        {t('menu:messages')}
        {counts.messages.unread > 0 && (
          <>
            {' '}
            <Badge 
              count={counts.messages.unread} 
              size="small" 
              style={{ backgroundColor: '#1890ff' }}
            />
            {counts.messages.new > 0 && (
              <Badge 
                count={`+${counts.messages.new}`} 
                size="small" 
                style={{ backgroundColor: '#52c41a', marginLeft: 4 }}
              />
            )}
          </>
        )}
      </span>
    ) : t('menu:messages'),
    'messages',
    <MailOutlined />,
    [
      // Право — на ЧЕТЫРЁХ пунктах, не на группе: «Список звонков» закрыт другим правом
      // (v4 RIGHT_CALL_LIST, MessageHtml.php:1563). Он же не даёт группе схлопнуться:
      // негейченный ребёнок остаётся видимым, и раскрывашка не станет пустой.
      getItem(
        counts?.messages && counts.messages.unread > 0
          ? `${t('menu:messages_inbox')} (${counts.messages.unread})`
          : t('menu:messages_inbox'),
        '/messages/inbox',
        undefined,
        undefined,
        undefined,
        { requiredPermission: 'customers.messages' }
      ),
      getItem(t('menu:messages_outbox'), '/messages/outbox', undefined, undefined, undefined, {
        requiredPermission: 'customers.messages',
      }),
      getItem(t('menu:messages_archive'), '/messages/archive', undefined, undefined, undefined, {
        requiredPermission: 'customers.messages',
      }),
      getItem(t('menu:messages_broadcast'), '/messages/broadcast', undefined, undefined, undefined, {
        requiredPermission: 'customers.messages',
      }),
      getItem(t('menu:messages_call_list'), '/messages/call-list', undefined, undefined, undefined, {
        requiredPermission: 'messages.calls.view',
      }),
    ],
    undefined,
    erpNavigationGate('messages')
  ),

  // Бухгалтерия
  getItem(
    t('menu:accounting'),
    'accounting',
    <AccountBookOutlined />,
    [
      getItem(t('menu:accounting_balance'), '/accounting/balance'),
      getItem(t('menu:accounting_good_standing'), '/accounting/balance-0-3'),
      getItem(t('menu:accounting_suspect'), '/accounting/suspect'),
      getItem(t('menu:accounting_hopeless'), '/accounting/hopeless'),
      getItem(t('menu:accounting_changes'), '/accounting/changes'),
      getItem(t('menu:accounting_fee'), '/accounting/fee'),
    ],
    undefined,
    { requiredPermission: 'accounting.view', ...erpNavigationGate('accounting') },
  ),

  // Интерфейс поддержки
  getItem(
    t('menu:support_interface'),
    'support',
    <WindowsOutlined />,
    [getItem(t('menu:support_main'), '/support')]
  ),

  // Сооружения связи
  getItem(
    t('menu:nodes'),
    'nodes',
    <ApartmentOutlined />,
    [
      getItem(t('menu:nodes_list'), '/nodes'),
    ]
  ),

  // Кабельная инфраструктура
  getItem(t('menu:cable'), 'cable', <ApiOutlined />, buildCableMenuChildren(options?.cableMenu, t), undefined, { requiredPermission: 'cable_lines.view' }),

  // Мониторинг
  getItem(
    t('menu:monitoring'),
    'monitoring',
    <RadarChartOutlined />,
    [
      getItem(t('menu:monitoring_services'), '/monitoring/services', <DashboardOutlined />),
      getItem(t('menu:monitoring_snmp'), '/monitoring/snmp', <HeatMapOutlined />),
      getItem(t('menu:monitoring_rabbitmq'), '/monitoring/rabbitmq'),
      getItem(t('menu:monitoring_redis'), '/monitoring/redis'),
      getItem(t('menu:monitoring_database'), '/monitoring/database'),
      getItem(t('menu:monitoring_grafana'), `external::${getGrafanaUrl()}`, <BarChartOutlined />),
      getItem(t('menu:monitoring_prometheus'), '/monitoring/prometheus', <DashboardOutlined />),
    ],
    undefined,
    { requiredPermission: 'database_monitoring.view' }
  ),

  // Оборудование
  ...(deviceMenuChildren.length
    ? [
        getItem(
          t('menu:devices'),
          'devices',
          <CloudOutlined />,
          deviceMenuChildren,
          undefined,
          { requiredPermission: 'equipment.view' }
        ),
      ]
    : []),

  // Журнал заданий (динамическое меню)
  (() => {
    const isDynamicTasksMenu = hasTaskMenuItems;
    const tasksLabelBase = taskMenuResult?.label ?? t('menu:tasks');
    const taskCounts = counts?.tasks;

    if (!isDynamicTasksMenu) {
      const label = taskCounts ? (
        <span>
          {tasksLabelBase}{' '}
          <Badge
            count={taskCounts.my}
            size="small"
            style={{ backgroundColor: '#722ed1' }}
            title={t('menu:tasks_badge_my')}
          />{' '}
          <Badge
            count={taskCounts.all}
            size="small"
            style={{ backgroundColor: '#1890ff' }}
            title={t('menu:tasks_badge_all')}
          />
        </span>
      ) : (
        tasksLabelBase
      );

      return getItem(label, 'tasks', <CheckSquareOutlined />, taskChildren, undefined, { requiredPermission: 'tasks.view' });
    }

    return {
      key: 'tasks',
      icon: <CheckSquareOutlined />,
      label: tasksLabelBase,
      type: undefined,
      children: taskChildren,
      requiredPermission: 'tasks.view',
    } as ExtendedMenuItem;
  })(),

  // Склад
  getItem(
    warehouseLabel,
    'warehouse',
    <InboxOutlined />,
    [
      ...warehouseChildren,
    ],
    undefined,
    { requiredPermission: 'inventory.view' }
  ),

  // Покрытие
  getItem(
    t('menu:map'),
    'map',
    <EnvironmentOutlined />,
    [
      getItem(t('menu:map_main'), '/map'),
      getItem(t('menu:map_buildings'), '/map/buildings'),
      getItem(t('menu:map_owners'), '/map/owners'),
      getItem(t('menu:map_tenants'), '/map/tenants'),
      getItem(t('menu:map_keys'), '/map/keys'),
      // Временно удалены: map_objects и города для быстрого перехода
      // Будет реализовано позже через отдельный механизм
    ],
    undefined,
    { requiredPermission: 'map.view', ...erpNavigationGate('map') }
  ),

  // Персонал
  getItem(
    t('menu:employees'),
    'employees',
    <UserOutlined />,
    [
      getItem(t('menu:employees_divisions'), '/employees/divisions', undefined, undefined, undefined, {
        requiredPermission: 'employee.view',
      }),
      getItem(t('menu:employees_timesheet'), '/employees/timesheet', undefined, undefined, undefined, {
        requiredPermission: 'employee.view',
        ...erpNavigationGate('employeeTimesheet'),
      }),
      getItem(t('menu:employees_reports'), '/employees/reports', undefined, undefined, undefined, {
        ...erpNavigationGate('employeeReports'),
      }),
      getItem(t('menu:employees_motivation'), '/employees/motivation', undefined, undefined, undefined, {
        ...erpNavigationGate('employeeMotivation'),
      }),
      getItem(t('menu:employees_vacation'), '/employees/vacation', undefined, undefined, undefined, {
        requiredPermission: 'employee.view',
        ...erpNavigationGate('employeeVacation'),
      }),
    ]
  ),

  // Автотранспорт. Группа без собственного права: /transport (флот) и
  // /transport-schedules (расписания) гейтятся разными легаси-правами (90/91 против
  // 105/106 — независимые пары, миграция v4 лишь backfill'ила 105/106 туда, где уже
  // было 90/91, разово, не как постоянный alias). Общий requiredPermission на группе
  // прятал бы расписания от оператора, у которого есть только 105/106 — тот же класс
  // бага, что и решение владельца 2026-07-26 по группе «Настройки» ниже.
  getItem(
    t('menu:transport'),
    'transport',
    <CarOutlined />,
    [
      getItem(t('menu:transport_list'), '/transport', undefined, undefined, undefined, {
        requiredPermission: 'transport.view',
      }),
      getItem(t('menu:transport_schedule'), '/transport-schedules', undefined, undefined, undefined, {
        requiredPermission: 'transport_schedule.view',
        ...erpNavigationGate('transportSchedule'),
      }),
    ],
    undefined,
    { requiresGatedChild: true, ...erpNavigationGate('transport') }
  ),

  // Рекламные кампании
  getItem(
    t('menu:advertisement'),
    'advertisement',
    <BellOutlined />,
    [
      getItem(t('menu:advertisement_list'), '/advertisement', undefined, undefined, undefined, {
        requiredPermission: 'advertising.view',
      }),
    ],
    undefined,
    erpNavigationGate('advertisement')
  ),

  // Блокнот
  getItem(
    t('menu:notepad'),
    'notepad',
    <FileTextOutlined />,
    [
      getItem(t('menu:notepad_list'), '/notepad'),
    ]
  ),

  // Статистика
  getItem(
    t('menu:statistics'),
    'statistics',
    <PieChartOutlined />,
    [
      getItem(t('menu:statistics_main'), '/statistics'),
    ]
  ),

  // Пользователи
  getItem(
    t('menu:users'),
    'users',
    <UserOutlined />,
    [
      getItem(t('menu:users_list'), '/users'),
      getItem(t('menu:users_profiles'), '/users/profiles'),
      getItem(t('menu:users_messages'), '/users/messages'),
      getItem(t('menu:users_history'), '/users/history'),
    ],
    undefined,
    { requiredPermission: 'employee.view' }
  ),

  // Настройки
  getItem(
    t('menu:settings'),
    'settings',
    <SettingOutlined />,
    settingsChildren,
    undefined,
    // Собственного права у группы НЕТ намеренно. Прежний гейт `system.settings`
    // существует в Go-каталоге (auth/domain/role.go) и висит на write-роутах настроек
    // задач, но в public.auth_item у него 0 строк — держать его не может никто, и
    // группа была недостижима кликом для всех, кроме админа (у админа hasPermission
    // замкнут на роль). Сеять право нельзя: вместе с ним ушло бы право на запись
    // справочников задач. Решение владельца 2026-07-26 — видимость по детям.
    { requiresGatedChild: true }
  ),

  // Отправить SMS
  getItem(t('menu:sms_send'), '/sms/send', <MessageOutlined />),
];
};

// Функция для добавления счётчиков к лейблам
export function addBadgeToLabel(label: string, count: number, color: string = '#1890ff') {
  return (
    <span>
      {label} <Badge count={count} size="small" style={{ backgroundColor: color }} />
    </span>
  );
}
